import { useAppNavigate } from "@/hooks/pane-routing";
import { useEntityNodeObjectActionHandlers } from "@/object-actions/handlers/entityNode";
import { useHydrateCanvasLinkEntity } from "@/pages/canvas/hooks/useHydrateCanvasLinkEntity";
import { useOpenCanvasLink } from "@/pages/canvas/hooks/useOpenCanvasLink";
import { isCanvasLinkEntity } from "@/pages/canvas/utils/canvasLinkEntity";
import { openEntityInDetailPane } from "@/pages/canvas/utils/openNodeInDetailPane";
import {
  openCanvasEntityTabUrl,
  resolveCanvasEntityOpenTabUrl,
} from "@/pages/canvas/utils/resolveCanvasEntityOpenTabUrl";
import { useOpenFileRecord } from "@/panes/file-record-detail";
import { type Entity, getEntityData } from "@cohesive/ui-components";
import { useCallback, useMemo } from "react";

export function useCanvasEntityNavigationHandlers(
  entity: Entity | null | undefined,
  canEdit: boolean,
  onDelete: () => void,
  onOpenOverride?: () => void,
) {
  const appNavigate = useAppNavigate();
  const openFileRecord = useOpenFileRecord();
  const openCanvasLink = useOpenCanvasLink();

  // A canvas card's "Open in new tab" is a workspace tab, not a browser tab:
  // it takes the in-app opener below and never resolves an external URL.
  // A link whose canvas is gone (trashed, or access lost) has nowhere to
  // open, so it offers no open at all — like an Unavailable file card — and
  // keeps only the actions that clean up the dead link. Read here, from the
  // same query the card renders against, so node, toolbar and menu agree.
  // Gone is a settled answer: a hydration still in flight stays openable.
  const canvasLink = entity && isCanvasLinkEntity(entity) ? entity : null;
  const { hydration: canvasLinkHydration } =
    useHydrateCanvasLinkEntity(canvasLink);
  const openableCanvasLink =
    canvasLink && !canvasLinkHydration?.isGone ? canvasLink : null;
  const openTabUrl = useMemo(
    () =>
      entity && !canvasLink ? resolveCanvasEntityOpenTabUrl(entity) : null,
    [entity, canvasLink],
  );

  const defaultOnOpen = useCallback(() => {
    if (!entity) return;

    const entityData = getEntityData(entity);
    if (!entityData?.id) return;

    if ("base" in entity && entity.base === "file_record") {
      openFileRecord(String(entityData.id));
      return;
    }

    openEntityInDetailPane(entity, appNavigate, {
      openCanvasLink: openableCanvasLink ? openCanvasLink : undefined,
    });
  }, [appNavigate, entity, openFileRecord, openCanvasLink, openableCanvasLink]);

  const onOpen = onOpenOverride ?? defaultOnOpen;

  const onOpenInNewTab = useCallback(() => {
    if (openableCanvasLink) {
      openCanvasLink(openableCanvasLink);
      return;
    }
    if (!openTabUrl) return;
    openCanvasEntityTabUrl(openTabUrl);
  }, [openableCanvasLink, openCanvasLink, openTabUrl]);

  const handlers = useEntityNodeObjectActionHandlers({
    entity,
    canEdit,
    onDelete,
    onOpen,
    onOpenInNewTab:
      openTabUrl || openableCanvasLink ? onOpenInNewTab : undefined,
  });

  return {
    handlers,
    hasWebLink: !!openTabUrl || !!openableCanvasLink,
    // A canvas link has no pane view: "Open in new tab" is its only open.
    hasDetailView: !!entity && !isCanvasLinkEntity(entity),
  };
}
